// AI Usage Logger Service
// Tracks and logs AI usage for analytics and cost monitoring
import { db, auth } from '../src/firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

export interface AIUsageLog {
  schoolId: string;
  schoolName: string;
  feature: 'research' | 'chat' | 'library' | 'exams';
  model: string;
  promptTokens?: number;
  completionTokens?: number;
  totalTokens: number;
  estimatedCost: number;
  userId: string;  // Required for Firestore security rules
  timestamp: any; // Firestore Timestamp
}

// Token pricing for Gemini models (accurate as of December 2024)
// Prices are per 1 MILLION tokens (USD)
// Source: https://ai.google.dev/pricing
const MODEL_PRICING: Record<string, { input: number; output: number }> = {
  // Gemini 2.5 Series (Latest)
  'gemini-2.5-flash-preview-05-20': { input: 0.15, output: 0.60 },
  'gemini-2.5-flash': { input: 0.15, output: 0.60 },
  'gemini-2.5-flash-lite': { input: 0.075, output: 0.30 },
  'gemini-2.5-pro-preview-05-06': { input: 1.25, output: 5.00 },
  'gemini-2.5-pro': { input: 1.25, output: 5.00 },

  // Gemini 2.0 Series
  'gemini-2.0-flash': { input: 0.10, output: 0.40 },
  'gemini-2.0-flash-lite': { input: 0.075, output: 0.30 },
  'gemini-2.0-flash-exp': { input: 0.10, output: 0.40 },
  'gemini-2.0-flash-thinking-exp': { input: 0.10, output: 0.40 },
  'gemini-2.0-flash-thinking-exp-01-21': { input: 0.10, output: 0.40 },

  // Gemini 1.5 Series
  'gemini-1.5-flash': { input: 0.075, output: 0.30 },
  'gemini-1.5-flash-8b': { input: 0.0375, output: 0.15 },
  'gemini-1.5-flash-latest': { input: 0.075, output: 0.30 },
  'gemini-1.5-pro': { input: 1.25, output: 5.00 },
  'gemini-1.5-pro-latest': { input: 1.25, output: 5.00 },

  // Experimental/Preview models (use conservative estimates)
  'gemini-exp': { input: 0.15, output: 0.60 },

  // Default pricing for unknown models (conservative estimate)
  'default': { input: 0.15, output: 0.60 }
};

/**
 * Estimate tokens in text
 * More accurate estimation: ~3.5 characters per token for English text
 * For multilingual/code content, this is adjusted to ~3 chars per token
 */
function estimateTokens(text: string): number {
  if (!text) return 0;

  // Check if text contains significant non-ASCII (multilingual) or code patterns
  const nonAsciiRatio = (text.match(/[^\x00-\x7F]/g) || []).length / text.length;
  const hasCodePatterns = /[{}\[\]();=<>]/.test(text);

  // Use 3 chars/token for code/multilingual, 3.5 for normal English
  const charsPerToken = (nonAsciiRatio > 0.1 || hasCodePatterns) ? 3 : 3.5;

  return Math.ceil(text.length / charsPerToken);
}

/**
 * Get pricing for a model, handling version suffixes and fallbacks
 */
function getModelPricing(modelName: string): { input: number; output: number } {
  // Direct match
  if (MODEL_PRICING[modelName]) {
    return MODEL_PRICING[modelName];
  }

  // Try removing version suffixes (e.g., gemini-2.0-flash-exp-01-21 -> gemini-2.0-flash-exp)
  const baseModel = modelName.replace(/-\d{2}-\d{2}$/, '');
  if (MODEL_PRICING[baseModel]) {
    return MODEL_PRICING[baseModel];
  }

  // Try matching by prefix (e.g., gemini-2.5-flash-* -> gemini-2.5-flash)
  for (const key of Object.keys(MODEL_PRICING)) {
    if (modelName.startsWith(key)) {
      return MODEL_PRICING[key];
    }
  }

  // Fallback to default
  return MODEL_PRICING['default'];
}

/**
 * Calculate cost based on model and token usage
 */
function calculateCost(model: string, promptTokens: number, completionTokens: number): number {
  const pricing = getModelPricing(model);

  // Convert to cost (pricing is per 1M tokens)
  const inputCost = (promptTokens / 1000000) * pricing.input;
  const outputCost = (completionTokens / 1000000) * pricing.output;

  return inputCost + outputCost;
}

/**
 * Get school info from auth claims
 */
async function getSchoolInfo(): Promise<{ schoolId: string; schoolName: string } | null> {
  const user = auth.currentUser;
  if (!user) return null;

  try {
    const idTokenResult = await user.getIdTokenResult();
    const claims = idTokenResult.claims;

    // Get default affiliation (check both schoolId and schoolID)
    const defaultAff = claims.defaultAffiliation as any;
    if (defaultAff?.schoolId || defaultAff?.schoolID) {
      const sid = defaultAff.schoolId || defaultAff.schoolID;
      return {
        schoolId: sid,
        schoolName: defaultAff.schoolName || sid
      };
    }

    // Fallback to first affiliation
    const affiliations = (claims.affiliations as any[]) || [];
    if (affiliations.length > 0) {
      const aff = affiliations[0];
      const sid = aff.schoolId || aff.schoolID;
      if (sid) {
        return {
          schoolId: sid,
          schoolName: aff.schoolName || sid
        };
      }
    }

    // Fallback to direct schoolID claim
    const directSchoolID = claims.schoolID as string;
    if (directSchoolID) {
      return {
        schoolId: directSchoolID,
        schoolName: directSchoolID
      };
    }
  } catch (error) {
    console.warn('Could not get school info from claims:', error);
  }

  return null;
}

/**
 * Log AI usage to Firestore
 */
export async function logAIUsage(
  feature: 'research' | 'chat' | 'library' | 'exams',
  model: string,
  promptText: string,
  responseText: string,
  schoolId?: string
): Promise<void> {
  try {
    // Must have authenticated user for Firestore rules
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.warn('No authenticated user, skipping AI usage logging');
      return;
    }

    // Get school info
    let schoolInfo = schoolId ? { schoolId, schoolName: schoolId } : await getSchoolInfo();

    if (!schoolInfo) {
      console.warn('No school info available, skipping AI usage logging');
      return;
    }

    // Estimate tokens
    const promptTokens = estimateTokens(promptText);
    const completionTokens = estimateTokens(responseText);
    const totalTokens = promptTokens + completionTokens;

    // Calculate cost
    const estimatedCost = calculateCost(model, promptTokens, completionTokens);

    // Prepare log entry - userId MUST match auth.uid for Firestore rules
    const logEntry: AIUsageLog = {
      schoolId: schoolInfo.schoolId,
      schoolName: schoolInfo.schoolName,
      feature,
      model,
      promptTokens,
      completionTokens,
      totalTokens,
      estimatedCost,
      userId: currentUser.uid,  // Required for Firestore security rules
      timestamp: serverTimestamp()
    };

    // Write to Firestore
    const logsRef = collection(db, 'aiUsageLogs');
    await addDoc(logsRef, logEntry);

    console.log(`AI usage logged: ${feature} - ${totalTokens} tokens - $${estimatedCost.toFixed(4)} for school ${schoolInfo.schoolId}`);
  } catch (error) {
    // Don't throw errors for logging failures - just log them
    console.error('Failed to log AI usage:', error);
  }
}

/**
 * Log AI usage with custom token counts (if available from API response)
 */
export async function logAIUsageWithTokens(
  feature: 'research' | 'chat' | 'library' | 'exams',
  model: string,
  promptTokens: number,
  completionTokens: number,
  schoolId?: string
): Promise<void> {
  try {
    // Must have authenticated user for Firestore rules
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.warn('No authenticated user, skipping AI usage logging');
      return;
    }

    // Get school info
    let schoolInfo = schoolId ? { schoolId, schoolName: schoolId } : await getSchoolInfo();

    if (!schoolInfo) {
      console.warn('No school info available, skipping AI usage logging');
      return;
    }

    const totalTokens = promptTokens + completionTokens;
    const estimatedCost = calculateCost(model, promptTokens, completionTokens);

    const logEntry: AIUsageLog = {
      schoolId: schoolInfo.schoolId,
      schoolName: schoolInfo.schoolName,
      feature,
      model,
      promptTokens,
      completionTokens,
      totalTokens,
      estimatedCost,
      userId: currentUser.uid,  // Required for Firestore security rules
      timestamp: serverTimestamp()
    };

    const logsRef = collection(db, 'aiUsageLogs');
    await addDoc(logsRef, logEntry);

    console.log(`AI usage logged: ${feature} - ${totalTokens} tokens - $${estimatedCost.toFixed(4)} for school ${schoolInfo.schoolId}`);
  } catch (error) {
    console.error('Failed to log AI usage:', error);
  }
}
